import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ComponentErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;
  private previousResetKeys: Array<string | number> = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
    this.previousResetKeys = props.resetKeys || [];
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Component Error Boundary caught an error:', error, errorInfo);
    }

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // In production and staging, send to Sentry (but not local development)
    if (
      process.env.REACT_APP_ENVIRONMENT === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'staging'
    ) {
      try {
        const Sentry = require('@sentry/react');
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
          tags: {
            error_boundary: 'component',
          },
        });
      } catch (sentryError) {
        // Ignore Sentry errors to prevent breaking error boundary functionality
        console.error('Failed to send error to Sentry:', sentryError);
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== this.previousResetKeys[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
        this.previousResetKeys = resetKeys;
      }
    }

    // Reset when any props change if resetOnPropsChange is true
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId !== null) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children, isolate } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return <>{fallback}</>;
      }

      // Default minimal error UI for components
      const errorUI = (
        <div className={`${isolate ? 'p-4' : 'p-8'} text-center bg-gray-50 rounded-lg`}>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Unable to load this section</h3>
          <p className="text-xs text-gray-500 mb-3">Please try refreshing the page</p>
          {process.env.NODE_ENV === 'development' && error && (
            <details className="text-left mt-2">
              <summary className="text-xs text-gray-600 cursor-pointer">Error details</summary>
              <pre className="mt-1 text-xs text-gray-500 overflow-auto bg-gray-100 p-2 rounded">
                {error.toString()}
              </pre>
            </details>
          )}
          <button
            onClick={this.resetErrorBoundary}
            className="mt-2 text-xs text-blue-600 hover:text-blue-500"
          >
            Try again
          </button>
        </div>
      );

      // If isolate is true, render error UI without affecting layout
      if (isolate) {
        return <div className="contents">{errorUI}</div>;
      }

      return errorUI;
    }

    return children;
  }
}
